import { Capacitor } from "@capacitor/core";
import { Geolocation } from "@capacitor/geolocation";
import LiveTracking from "../native/liveTracking";

export const isNative = Capacitor.isNativePlatform();

/**
 * Ask for whatever permissions the current platform needs before a run can
 * be recorded: on native this also covers notification permission (Android
 * 13+) since tracking runs inside a foreground-service notification.
 */
export async function requestTrackingPermissions() {
  if (isNative) {
    const result = await LiveTracking.requestPermissions();
    return result.location === "granted";
  }
  return true; // web: the browser prompts lazily on first watchPosition/getCurrentPosition call
}

/**
 * One-shot position fix, used to center the map before a run starts.
 * Works uniformly on web + native via the Capacitor Geolocation plugin.
 */
export async function getCurrentPosition() {
  const pos = await Geolocation.getCurrentPosition({ enableHighAccuracy: true, timeout: 10000 });
  return { lat: pos.coords.latitude, lng: pos.coords.longitude };
}

/**
 * Starts the live position stream for an in-progress run.
 *
 * - Native: hands GPS tracking to the native foreground service / Live
 *   Activity (survives screen-lock & backgrounding) and relays fixes here.
 * - Web: falls back to the standard browser watchPosition API.
 *
 * `onPosition({ lat, lng, accuracy, timestamp })` is called on every fix.
 * `onError(message)` is called on failures.
 * Returns a handle to pass to `stopTracking`.
 */
export async function startTracking({ activityType, onPosition, onError }) {
  if (isNative) {
    const listener = await LiveTracking.addListener("locationUpdate", onPosition);
    const errorListener = await LiveTracking.addListener("trackingError", (e) => onError?.(e.message));
    await LiveTracking.start({ activityType });
    return { native: true, listener, errorListener };
  }

  if (!navigator.geolocation) {
    onError?.("Dein Browser unterstützt keine Standortabfrage.");
    return { native: false, watchId: null };
  }
  const watchId = navigator.geolocation.watchPosition(
    (pos) => onPosition({ lat: pos.coords.latitude, lng: pos.coords.longitude, accuracy: pos.coords.accuracy, timestamp: pos.timestamp }),
    (err) => onError?.(err.code === 1 ? "Standortfreigabe verweigert. Bitte im Browser erlauben." : "Standort konnte nicht ermittelt werden."),
    { enableHighAccuracy: true, maximumAge: 1000, timeout: 15000 }
  );
  return { native: false, watchId };
}

/** Pushes fresh stats to the Android notification / iOS Live Activity. No-op on web. */
export async function updateTrackingStats({ distanceLabel, durationLabel, paceLabel }) {
  if (!isNative) return;
  try {
    await LiveTracking.updateStats({ distanceLabel, durationLabel, paceLabel });
  } catch (e) {
    /* ignore: live activity is a nice-to-have, never block recording on it */
  }
}

export async function stopTracking(handle) {
  if (!handle) return;
  if (handle.native) {
    await handle.listener?.remove();
    await handle.errorListener?.remove();
    await LiveTracking.stop();
  } else if (handle.watchId != null) {
    navigator.geolocation.clearWatch(handle.watchId);
  }
}
